import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { Search, SlidersHorizontal, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FundsPage = () => {
  const { t, i18n } = useTranslation();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');
  const [sortBy, setSortBy] = useState('yield');

  useEffect(() => {
    const fetchFunds = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/funds`);
        setFunds(response.data);
      } catch (error) {
        console.error('Error fetching funds:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFunds();
  }, []);

  const getFundName = (fund) => {
    if (i18n.language === 'en' && fund.name_en) return fund.name_en;
    if (i18n.language === 'pt' && fund.name_pt) return fund.name_pt;
    return fund.name;
  };

  const getFundDescription = (fund) => {
    if (i18n.language === 'en' && fund.description_en) return fund.description_en;
    if (i18n.language === 'pt' && fund.description_pt) return fund.description_pt;
    return fund.description;
  };

  const getRiskLabel = (level) => {
    const labels = {
      low: t('funds.low'),
      medium: t('funds.medium'),
      high: t('funds.high')
    };
    return labels[level] || level;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Filter and sort funds
  const filteredFunds = funds
    .filter((fund) => {
      const name = getFundName(fund).toLowerCase();
      const matchesSearch = name.includes(searchTerm.toLowerCase());
      const matchesRisk = riskFilter === 'all' || fund.risk_level === riskFilter;
      return matchesSearch && matchesRisk;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'yield':
          return b.target_yield - a.target_yield;
        case 'raised':
          return b.total_raised - a.total_raised;
        case 'min':
          return a.min_investment - b.min_investment;
        default:
          return 0;
      }
    });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Skeleton className="h-10 w-48 mb-8 bg-[#141414]" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-80 bg-[#141414]" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl text-white mb-4">
            {t('funds.title')}
          </h1>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              type="text"
              placeholder={t('funds.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#141414] border-white/10 text-white rounded-sm focus:border-white/30"
              data-testid="fund-search"
            />
          </div>
          
          <Select value={riskFilter} onValueChange={setRiskFilter}>
            <SelectTrigger className="w-full sm:w-[180px] bg-[#141414] border-white/10 text-white rounded-sm" data-testid="risk-filter">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Risk Level" />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1C] border-white/10">
              <SelectItem value="all" className="text-zinc-300 hover:text-white">{t('funds.allRisks')}</SelectItem>
              <SelectItem value="low" className="text-zinc-300 hover:text-white">{t('funds.low')}</SelectItem>
              <SelectItem value="medium" className="text-zinc-300 hover:text-white">{t('funds.medium')}</SelectItem>
              <SelectItem value="high" className="text-zinc-300 hover:text-white">{t('funds.high')}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full sm:w-[180px] bg-[#141414] border-white/10 text-white rounded-sm" data-testid="sort-by">
              <SelectValue placeholder={t('funds.sortBy')} />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1C] border-white/10">
              <SelectItem value="yield" className="text-zinc-300 hover:text-white">{t('funds.targetYield')}</SelectItem>
              <SelectItem value="raised" className="text-zinc-300 hover:text-white">{t('funds.totalRaised')}</SelectItem>
              <SelectItem value="min" className="text-zinc-300 hover:text-white">{t('funds.minInvestment')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Funds Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredFunds.map((fund) => (
            <motion.div key={fund.id} variants={itemVariants}>
              <Link to={`/funds/${fund.id}`}>
                <Card className="card-presage rounded-sm overflow-hidden group cursor-pointer h-full" data-testid={`fund-card-${fund.id}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={fund.image_url || 'https://images.pexels.com/photos/2275008/pexels-photo-2275008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                      alt={getFundName(fund)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {fund.is_guaranteed && (
                      <div className="absolute top-3 right-3 bg-[#D4AF37] text-black text-xs px-2 py-1 rounded-sm font-semibold flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        {t('funds.guaranteed')}
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3">
                      <span className={`px-2 py-1 text-xs rounded-sm risk-${fund.risk_level}`}>
                        {getRiskLabel(fund.risk_level)}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-white text-lg mb-2 group-hover:text-zinc-300 transition-colors">
                      {getFundName(fund)}
                    </h3>
                    <p className="text-zinc-500 text-sm mb-4 line-clamp-2">
                      {getFundDescription(fund)}
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="data-label">{t('funds.targetYield')}</p>
                        <p className="data-value text-xl text-emerald-500">{fund.target_yield}%</p>
                      </div>
                      {fund.actual_yield && (
                        <div>
                          <p className="data-label">{t('funds.actualYield')}</p>
                          <p className="data-value text-xl text-white">{fund.actual_yield}%</p>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-white/10">
                      <div>
                        <p className="data-label">{t('funds.minInvestment')}</p>
                        <p className="data-value text-white">{formatCurrency(fund.min_investment)}</p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                        data-testid={`invest-btn-${fund.id}`}
                      >
                        {t('funds.invest')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {filteredFunds.length === 0 && (
          <div className="text-center py-16">
            <p className="text-zinc-500">No funds found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FundsPage;
